import { useTRPC } from "@/trpc";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Select } from "@/components/Selects/select";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Button } from "../ui/button";
import { DialogFooter } from "../ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { useState } from "react";
import { queryClient } from "@/ajax/queryClient";
import DatePicker from "@/components/DatePicker";
import { CalendarIcon } from "lucide-react";
import { format, parse } from "date-fns";
import { Switch } from "../ui/switch";
import { FileType } from "@/features/files/types/file-type";
import Loading from "../Loading";

type FileTypeOption = {
  value: string;
  label: string;
};
type FilePreviewEditFormValues = {
  title: string;
  type: FileTypeOption;
  expires_at: Date | null;
  notify_expiry: boolean;
};

export function FilePreviewEdit({
  file,
  onClose,
  userId,
  organizationBusinessUnitId,
}: {
  file: FileType;
  onClose: () => void;
  userId: string;
  organizationBusinessUnitId?: string;
}) {
  const [error, setError] = useState<string | null>(null);
  const trpc = useTRPC();
  const {
    data: fileTypesData,
    isLoading: isLoadingFileTypes,
    error: fileTypesError,
  } = useQuery(trpc.credentialFiles.getFileTypes.queryOptions());

  const fileTypes =
    fileTypesData?.map((type) => ({
      value: String(type.id),
      label: type.name,
    })) ?? [];

  const form = useForm<
    FilePreviewEditFormValues,
    unknown,
    FilePreviewEditFormValues
  >({
    defaultValues: {
      title: file.generated_title || file.file_name,
      type: {
        value: String(file.type_id),
        label: file.type_name,
      },
      expires_at: file.expires_at ? new Date(file.expires_at) : null,
      notify_expiry: Boolean(file.expiry_notifications_enabled),
    },
  });

  const updateFileMutation = useMutation(
    trpc.credentialFiles.updateFile.mutationOptions({
      onSuccess: async () => {
        setError(null);
        await queryClient.invalidateQueries({
          queryKey: trpc.credentialFiles.getFilesCheckPermission.queryKey(),
        });
        onClose();
      },
      onError: (error) => {
        setError(
          "An error occurred while updating the file. Please try again.",
        );
      },
    }),
  );

  const onSubmit = (values: FilePreviewEditFormValues) => {
    updateFileMutation.mutate({
      id: file.id,
      switchUserId: userId,
      organizationBusinessUnitId,
      title: values.title,
      typeId: Number(values.type.value),
      expiresAt: values.expires_at
        ? format(values.expires_at, "yyyy-MM-dd")
        : null,
      expiryNotificationsEnabled: values.notify_expiry,
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open={true} onOpenChange={handleOpenChange}>
      <DialogContent className="min-w-[400px]">
        <DialogHeader>
          <DialogTitle>Edit Document</DialogTitle>
          <DialogDescription>
            Edit the title and type of your document.
          </DialogDescription>
        </DialogHeader>
        {isLoadingFileTypes ? (
          <div className="flex items-center justify-center py-12">
            <Loading className="h-12 w-12" />
          </div>
        ) : fileTypesError ? (
          <div className="text-red-500 py-6 text-center">
            Unable to load file types. Please try again later.
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <FormControl>
                      <Select
                        options={fileTypes}
                        {...field}
                        isClearable
                        placeholder="Select a category"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="expires_at"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Expiration Date</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <DatePicker
                          selected={field.value}
                          onChange={(date) => field.onChange(date)}
                          customInput={
                            <Input
                              value={
                                field.value
                                  ? format(field.value, "MM-dd-yyyy")
                                  : ""
                              }
                              onChange={(e) => {
                                const date = parse(
                                  e.target.value,
                                  "MM-dd-yyyy",
                                  new Date(),
                                );
                                if (
                                  date instanceof Date &&
                                  !isNaN(date.getTime())
                                ) {
                                  field.onChange(date);
                                }
                              }}
                            />
                          }
                          dateFormat="MM-dd-yyyy"
                          wrapperClassName="w-full"
                        />
                        <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.watch("expires_at") && (
                <FormField
                  control={form.control}
                  name="notify_expiry"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center rounded-lg border p-4">
                      <FormControl className="mr-4">
                        <Switch
                          checked={field.value ?? false}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">
                          Expiry Notifications
                        </FormLabel>
                        <FormDescription>
                          Get notified when this document is about to expire
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              )}
              {error && <div className="text-red-500">{error}</div>}
              <DialogFooter className="flex sm:flex-row gap-2 sm:gap-0">
                <Button type="submit" disabled={updateFileMutation.isPending}>
                  {updateFileMutation.isPending ? "Saving..." : "Save changes"}
                </Button>
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
